import { Permissions } from '../../models/enums/permissions';
import { AnyOrdering, AnyApproval, AnyRouting, AnyProduction, AnyShipping, AnyInvoicing, AnyCommunication, AnyInventory, AnySupport } from '../../models/enums/permission-groups';
import { Routes } from "../../utils/app-constants";
import { INavbarGroupLinkProps, NavbarGroupProps } from "../navbar/Navbar";

/**
 * This routes array is responsible for building/controlling
 * the left hand navivation menu.
 * 
 * Each top level module is an element in the array.  That top
 * level module has a sectionPermissions property that
 * is an OR'd list of permission that are required to see that
 * node.
 * 
 * A user needs to have a permission in the sectionPermission and
 * in the links array to see the element in the navigation menu.
 * 
 * To actually access the page that is linked, RouteTable.tsx 
 * needs to be updated with the proper permissions as well.
 * 
 */

 // #region Order Route Config

const orderRoute = new NavbarGroupProps();

    orderRoute.title = 'Ordering';
    orderRoute.icon = 'la-shopping-cart';
    orderRoute.requiresServiceCenter = true;
    orderRoute.sectionPermissions = [
        ...AnyOrdering
    ];
    orderRoute.links = [
        {
            exact: true,
            path: Routes.ORDERING_ORDERS,
            title: 'Orders',
            // Just a placeholder to hide it from service center. Final permission TBD.
            permissions: [
                Permissions.OrderingOrders
            ],
            requiredServiceCenters: []
        },        
        {
            exact: true,
            path: Routes.ORDERING_BACKORDER,
            title: 'Backorder',
            // Just a placeholder to hide it from service center. Final permission TBD.
            permissions: [
                Permissions.OrderingBackorder
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.ORDERING_SYNC,
            title: 'Sync',
            permissions: [
                Permissions.OrderingSync
            ],
            requiredServiceCenters: []
        }
    ];

// #endregion

// #region Approvals Route Config

const approvalsRoute = new NavbarGroupProps();

    approvalsRoute.title = 'Approvals';
    approvalsRoute.icon = 'la-list-alt';
    approvalsRoute.requiresServiceCenter = true;
    // Shown to anyone who can get to at least one of the review pages
    approvalsRoute.sectionPermissions = [
        ...AnyApproval
    ];
    approvalsRoute.links = [
        {
            exact: true,
            path: Routes.APPROVALS_CREDIT_HOLD,
            title: 'Credit Hold',
            permissions: [
                Permissions.ApprovalsCreditHoldView
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.APPROVALS_ENGINEERING,
            title: 'Engineering',
            permissions: [
                Permissions.ApprovalsEngineering
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.APPROVALS_WINDLOAD,
            title: 'Wind Load',
            permissions: [
                Permissions.ApprovalsWindLoad
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.APPROVALS_CUSTOMER_SERVICE,
            title: 'Customer Service',
            permissions: [
                Permissions.ApprovalsCustomerService
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.APPROVALS_ADDRESSES,
            title: 'Addresses',
            permissions: [
                Permissions.ApprovalsAddressesView
            ],
            requiredServiceCenters: []
        },
    ];

// #endregion

// #region Routing Route Config

const routingRoute = new NavbarGroupProps();

    routingRoute.title = 'Routing';
    routingRoute.icon = 'la-map-signs';
    routingRoute.requiresServiceCenter = true;
    routingRoute.sectionPermissions = [
        ...AnyRouting
    ];
    routingRoute.links = [
        {
            exact: true,
            path: Routes.ROUTING_ROUTING,
            title: 'Routing',
            permissions: [
                Permissions.RoutingRoutingView
            ],
            requiredServiceCenters: []
        }
    ];

// #endregion

// #region Production Route Config 
const productionRoute = new NavbarGroupProps();

    productionRoute.title = 'Production';
    productionRoute.icon = 'la-cube';
    productionRoute.requiresServiceCenter = true;
    productionRoute.sectionPermissions = [
        ...AnyProduction
    ];
    productionRoute.links = [
        {
            exact: true,
            path: Routes.PRODUCTION_PLANNING,
            title: 'Planning',
            permissions: [
                Permissions.ProductionMetricsPickupsView,
                Permissions.ProductionMetricsDeliveriesView,
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.PRODUCTION_SCHEDULE_CDOEM,
            title: 'Schedule (CD/OEM)',
            permissions: [
                Permissions.ProductionScheduleView
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.PRODUCTION_SCHEDULE_RS,
            title: 'Schedule (RS)',
            permissions: [
                Permissions.ProductionScheduleRsView
            ],
            requiredServiceCenters: []
        }
    ];

// #endregion

// #region Shipping Route Config
const shippingRoute = new NavbarGroupProps();

    shippingRoute.title = 'Shipping';
    shippingRoute.icon = 'la-truck';
    shippingRoute.requiresServiceCenter = true;
    shippingRoute.sectionPermissions = [
        ...AnyShipping
    ],
    shippingRoute.links = [
            {
                exact: true,
                path: Routes.SHIPPING_TRUCKS,
                title: 'Trucks',
                permissions: [
                    Permissions.ShippingTrucksView
                ],
                requiredServiceCenters: []
            },
            {
                exact: true,
                path: Routes.SHIPPING_CONTAINERS,
                title: 'Containers',
                permissions: [
                    Permissions.ShippingContainersView
                ],
                requiredServiceCenters: []
            },
            {
                exact: true,
                path: Routes.SHIPPING_PICKUPS,
                title: 'Pickups',
                permissions: [
                    Permissions.ShippingPickupsView
                ],
                requiredServiceCenters: []
            },
            {
                exact: true,
                path: Routes.SHIPPING_FEDEX,
                title: 'Fedex',
                permissions: [
                    Permissions.ShippingFedexView
                ],
                requiredServiceCenters: []
            },
            {
                exact: true,
                path: Routes.SHIPPING_FLEET,
                title: 'Fleet',
                permissions: [
                    Permissions.ShippingFleetDriversView,
                    Permissions.ShippingFleetAssetsView
                ],
                requiredServiceCenters: []
            } as INavbarGroupLinkProps
    ];

// #endregion

// #region Inventory
const inventoryRoute = new NavbarGroupProps();

inventoryRoute.title = 'Inventory';
inventoryRoute.icon = 'la-clipboard';
inventoryRoute.requiresServiceCenter = false;
inventoryRoute.sectionPermissions = [
    ...AnyInventory
];
inventoryRoute.links = [
    {
        exact: true,
        path: Routes.INVENTORY_PHYSICAL,
        title: 'Physical',
        permissions: [
            ...AnyInventory
        ],
        requiredServiceCenters: []
    }
];

// #endregion

// #region Invoicing Route Config
const invoiceRoute = new NavbarGroupProps();

    invoiceRoute.title = 'Invoicing';
invoiceRoute.icon = 'la-dollar';
    invoiceRoute.requiresServiceCenter = true;
    invoiceRoute.sectionPermissions = [
        ...AnyInvoicing
    ];    
    invoiceRoute.links = [
        {
            exact: true,
            path: Routes.INVOICING_INVOICES,
            title: 'Invoices',
            permissions: [
                Permissions.InvoicingInvoices
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.INVOICING_CREDITS,
            title: 'Credits',
            permissions: [
                Permissions.InvoicingCredits
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.INVOICING_BILLING_ONLY,
            title: 'Billing Only',
            permissions: [
                Permissions.InvoicingBillingOnlyView
            ],
            requiredServiceCenters: []
        }
    ];

// #endregion

// #region Communication Route Config
const communicationRoute = new NavbarGroupProps();

    communicationRoute.title = 'Communications';
    communicationRoute.icon = 'la-comments';
    communicationRoute.requiresServiceCenter = true;
    communicationRoute.sectionPermissions = [
        ...AnyCommunication
    ];    
    communicationRoute.links = [
        {
            exact: true,
            path: Routes.COMMUNICATIONS_SHIPMENTS,
            title: 'Shipments',
            permissions: [
                Permissions.CommunicationsShipmentsTrucksView,
                Permissions.CommunicationsShipmentsContainersView
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.COMMUNICATIONS_MAIL_TRIAGE,
            title: 'Mail Triage',
            permissions: [
                Permissions.CommunicationsMailTriageView
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.COMMUNICATIONS_ACTIVITY_FEED,
            title: 'Activity Feed',
            permissions: [
                Permissions.CommunicationsActivityFeedView
            ],
            requiredServiceCenters: []
        }
    ];

// #endregion

// #region Suport Route Config
const supportRoute = new NavbarGroupProps();

    supportRoute.title = 'Support';
    supportRoute.icon = 'la-wrench';
    supportRoute.requiresServiceCenter = false;
    supportRoute.sectionPermissions = [
        ...AnySupport
    ];    
    supportRoute.links = [
        {
            exact: true,
            path: Routes.SUPPORT_DEALERS,
            title: 'Dealers',
            permissions: [
                Permissions.SupportDealersView
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.SUPPORT_ACTIVITY_FEED,
            title: 'Activity',
            permissions: [
                Permissions.SupportActivityFeed
            ],
            requiredServiceCenters: []
        },
        {
            exact: true,
            path: Routes.SUPPORT_USERS,
            title: 'Users',
            permissions: [
                Permissions.SupportUsersView
            ],
            requiredServiceCenters: []
        }
    ];

// #endregion

export const routes = [
    orderRoute,
    approvalsRoute,
    routingRoute,
    productionRoute,
    shippingRoute,
    invoiceRoute,
    communicationRoute,
    inventoryRoute,
    supportRoute
] as NavbarGroupProps[];
